import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Clapperboard } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";

export const VideoSpot = () => {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [ready, setReady] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  // Autoplay (muted) when the player scrolls into view; pause when it leaves.
  useEffect(() => {
    const wrap = wrapRef.current;
    const vid = videoRef.current;
    if (!wrap || !vid || !ready) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.5) {
          vid.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
        } else {
          vid.pause();
          setPlaying(false);
        }
      },
      { threshold: [0, 0.5, 1] }
    );
    io.observe(wrap);
    return () => io.disconnect();
  }, [ready]);

  const toggle = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) {
      vid.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      vid.pause();
      setPlaying(false);
    }
  };

  return (
    <section data-testid="video-spot-section" className="bg-slate-50 py-24 lg:py-32">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue sm:text-sm">See how it works</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
            Thirty seconds at your front door
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-600">
            Watch a real Oxford pickup: our driver arrives in your window, grabs the bag from your
            doorstep, and brings everything back fresh about 48 hours later. No errands, no dry-cleaner
            detour — ever.
          </p>
          <ul className="mt-6 space-y-3 text-base font-semibold text-brand-navy">
            <li className="flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-fall-amber" /> Free pickup &amp; delivery, always</li>
            <li className="flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-fall-amber" /> Text updates when we&apos;re close</li>
            <li className="flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-fall-amber" /> Fresh-pressed return in ~48 hours</li>
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <div ref={wrapRef} className="mx-auto w-full max-w-[320px]">
            <div
              data-testid="video-player-frame"
              className="relative aspect-[9/16] overflow-hidden rounded-[2.5rem] border-[10px] border-brand-navy bg-brand-navy shadow-2xl shadow-brand-navy/40"
            >
              <video
                ref={videoRef}
                data-testid="video-player"
                className="h-full w-full object-cover"
                muted={muted}
                loop
                playsInline
                preload="none"
                poster="/images/video_poster.webp"
                onError={() => setReady(false)}
                onClick={toggle}
              >
                <source src="/videos/how-it-works.mp4" type="video/mp4" />
              </video>

              {!ready && (
                <div data-testid="video-placeholder" className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-brand-navy-light to-brand-navy p-6 text-center">
                  <img src="/images/deliverguy.jpg" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-25" />
                  <div className="relative">
                    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-fall-amber/20 text-fall-amber">
                      <Clapperboard className="h-8 w-8" />
                    </span>
                    <p className="mt-4 font-display text-lg font-bold text-white">Video coming soon</p>
                    <p className="mt-1 text-xs font-semibold text-slate-300">A real 30-second Oxford pickup, filmed at your door</p>
                  </div>
                </div>
              )}

              {ready && (
                <>
                  <button
                    data-testid="video-play-toggle"
                    onClick={toggle}
                    aria-label={playing ? "Pause video" : "Play video"}
                    className={`absolute inset-0 flex items-center justify-center transition-opacity ${playing ? "opacity-0 hover:opacity-100" : "opacity-100"}`}
                  >
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-fall-amber/90 shadow-xl">
                      {playing ? <Pause className="h-7 w-7 text-brand-navy" /> : <Play className="ml-1 h-7 w-7 text-brand-navy" />}
                    </span>
                  </button>
                  <button
                    data-testid="video-mute-toggle"
                    onClick={() => setMuted((m) => !m)}
                    aria-label={muted ? "Unmute video" : "Mute video"}
                    className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur"
                  >
                    {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                  </button>
                </>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
